import * as React from 'react';
import { useState, useEffect } from 'react';
import { Redirect } from 'react-router';
import {
  CollectionPreferences,
  CollectionPreferencesProps,
  Pagination,
  PropertyFilter,
  Table,
  Flashbar,
  Header,
} from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';
import { useCollection } from '@amzn/awsui-collection-hooks';

import { largePageSizePreference, defaultWrapLinesPreference, i18nStrings, paginationLabels } from 'src/commons/tables';

import { listDatabases } from 'src/api/catalog';
import { TableProps } from '@amzn/awsui-components-react-v3/polaris/table/interfaces';
import { compareBy } from '../utils/sorting';
import {
  DATA_LOAD_LIMIT,
  DATABASE_LOAD_LIMIT,
  DZ_REDSHIFT_DATASOURCE_ID,
  REDSHIFT_DATASOURCE_ID,
  TABLE_CONTENT_TYPE,
} from 'src/commons/constants';
import { createCatalogDetailLink, createDatabaseDetailLink } from 'src/routes';
import { DefaultRouteProps, keyForCatalogNameMap } from 'src/commons/common';
import { EmptyState } from 'src/commons/EmptyState';

export interface BrowseDatabasesTableProps extends DefaultRouteProps {
  setContentType: any;
  title: string;
  loading?: boolean;
  filter?: object[];
  doNotLinkCatalogs?: boolean;
}

const BrowseDatabasesTable = (props: BrowseDatabasesTableProps) => {
  const [allItems, setItems] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [redirect] = useState<string>(undefined);
  const [databasesLoading, setDatabasesLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationDisabled, setPaginationDisabled] = useState(false);
  const [nextToken, setNextToken] = useState<string>(null);
  const [allDataLoaded, setAllDataLoaded] = useState(false);
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 25,
  });

  const columnDefinitions: TableProps.ColumnDefinition<any>[] = [
    {
      id: 'databaseName',
      header: 'Database name',
      cell: (item) => (
        <Link
          href={createDatabaseDetailLink(
            item?.dataSourceId,
            item?.catalogId,
            item?.clusterId,
            item?.redshiftWorkgroupName,
            item?.databaseName,
            item?.region,
          )}
        >
          {item.databaseName}
        </Link>
      ),
      minWidth: '200px',
    },
    {
      id: 'description',
      header: 'Description',
      cell: (item) => item.description,
      minWidth: '200px',
    },
    {
      id: 'catalogDisplayName',
      header: 'Catalog name',
      cell: (item) =>
        props.doNotLinkCatalogs ? (
          getCatalogName(item.catalogId, item.region, item.clusterId, item.redshiftWorkgroupName, item.dataSourceId)
        ) : (
          <Link
            href={createCatalogDetailLink(
              item?.dataSourceId,
              item?.catalogId,
              item?.clusterId,
              item?.redshiftWorkgroupName,
              item?.region,
            )}
          >
            {item.catalogDisplayName}
          </Link>
        ),
      minWidth: '100px',
      sortingField: 'catalogDisplayName',
      sortingComparator: compareBy('catalogDisplayName', 'databaseName'),
    },
  ];

  // simplifies the database objects in order to make the rest of the code a bit cleaner
  const flatten = (items) => {
    const flat = items.map((item) => ({
      databaseName: item.DatabaseName,
      catalogId: item.CatalogId,
      clusterId: item.ClusterIdentifier,
      redshiftWorkgroupName: item.RedshiftWorkgroupName,
      region: item.Region,
      catalogDisplayName: getCatalogName(
        item.CatalogId,
        item.Region,
        item.ClusterIdentifier,
        item.RedshiftWorkgroupName,
        item.DataSourceId,
      ),
      description: item.Description,
      dataSourceId: item.DataSourceId,
    }));
    return flat;
  };

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
    fetchAllData();
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [props.filter]);

  // Fetch databasaes and then their catalogs
  const fetchAllData = async () => {
    await fetchDatabases();
  };

  const fetchDatabases = async () => {
    setDatabasesLoading(true);
    try {
      if (props.filter) {
        let databaseList = [];
        setTotalCount(props.filter.length);
        for (let index = 0; index < props.filter.length; index += DATABASE_LOAD_LIMIT) {
          let databases = await listDatabases({
            DatabaseKeyList: props.filter.slice(index, index + DATABASE_LOAD_LIMIT),
            Limit: DATABASE_LOAD_LIMIT,
            IncludeTotalCount: true,
          });
          databaseList.push(
            ...databases.DatabaseInfoList.filter(
              (item) => ![REDSHIFT_DATASOURCE_ID, DZ_REDSHIFT_DATASOURCE_ID].includes(item.DataSourceId),
            ),
          );
        }
        setNextToken(null);
        setAllDataLoaded(true);
        setItems(databaseList);
      } else {
        let databases = await listDatabases({ Limit: DATA_LOAD_LIMIT, IncludeTotalCount: true });
        setNextToken(databases.NextToken);
        if (databases.NextToken == null) {
          setAllDataLoaded(true);
        }
        let databaseList = databases.DatabaseInfoList.filter(
          (item) => ![REDSHIFT_DATASOURCE_ID, DZ_REDSHIFT_DATASOURCE_ID].includes(item.DataSourceId),
        );
        setTotalCount(databases.TotalCount);
        setItems(databaseList);
      }
    } catch (e) {
      setDatabasesLoading(false);
      console.log('Exception when fetch databases', e);
      setNotifications([
        {
          type: 'error',
          content: `Error when trying to fetch databases: ${e.message}`,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    }
    setDatabasesLoading(false);
  };

  const loadMoreData = async () => {
    if (props.filter) {
      return;
    }
    setPaginationDisabled(true);
    setDatabasesLoading(true);
    let databases = await listDatabases({ Limit: DATA_LOAD_LIMIT, NextToken: nextToken });
    setNextToken(databases.NextToken);
    let databaseList = databases.DatabaseInfoList.filter(
      (item) => ![REDSHIFT_DATASOURCE_ID, DZ_REDSHIFT_DATASOURCE_ID].includes(item.DataSourceId),
    );
    let currentItems = allItems;
    currentItems.push(...databaseList);
    setItems(currentItems);
    if (databases.NextToken == null) {
      setAllDataLoaded(true);
    }
    setDatabasesLoading(false);
    setPaginationDisabled(false);
  };

  const getCatalogName = (
    awsAccountId: string,
    region: string,
    clusterIdentifier: string,
    redshiftWorkgroupName: string,
    dataSourceId: string,
  ) => {
    return (
      props.catalogMap.get(
        keyForCatalogNameMap(dataSourceId, awsAccountId, clusterIdentifier, redshiftWorkgroupName, region),
      ) ?? awsAccountId
    );
  };

  const { items, collectionProps, paginationProps, propertyFilterProps, filteredItemsCount } = useCollection(
    flatten(allItems),
    {
      filtering: {
        noMatch: {}, // filtering is disabled
        empty: <EmptyState title='No databases' subtitle='No databases were found.' />,
      },
      pagination: { pageSize: preferences.pageSize },
      sorting: {},
      selection: {},
      propertyFiltering: {
        filteringProperties: [
          {
            propertyLabel: 'Catalog name',
            key: 'catalogDisplayName',
            groupValuesLabel: 'Catalog names',
          },
        ],
      },
    },
  );

  const handlePageChange = async (e) => {
    if (paginationProps.currentPageIndex == paginationProps.pagesCount && !allDataLoaded) {
      await loadMoreData();
    }
    if (
      paginationProps.currentPageIndex != paginationProps.pagesCount ||
      (e.detail.currentPageIndex <= paginationProps.pagesCount &&
        e.detail.currentPageIndex != paginationProps.currentPageIndex)
    ) {
      paginationProps.onChange(e);
    }
  };

  if (redirect) return <Redirect push to={redirect} />;

  return (
    <>
      <Flashbar items={notifications} />
      <Table
        {...collectionProps}
        loadingText='Loading databases...'
        columnDefinitions={columnDefinitions}
        items={items}
        wrapLines={preferences.wrapLines}
        resizableColumns={true}
        header={
          <Header variant={'h2'} counter={`(${totalCount}${!allDataLoaded && props.filter ? '+' : ''})`}>
            {props.title}
          </Header>
        }
        loading={props.loading || databasesLoading}
        filter={
          <PropertyFilter
            {...propertyFilterProps}
            disabled={true}
            i18nStrings={{
              ...i18nStrings,
              filteringPlaceholder: 'To discover databases, use search page instead.',
            }}
            countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}`}
          />
        }
        preferences={
          <CollectionPreferences
            title={'Preferences'}
            confirmLabel={'Confirm'}
            cancelLabel={'Cancel'}
            preferences={preferences}
            onConfirm={({ detail }) => setPreferences(detail)}
            pageSizePreference={largePageSizePreference}
            wrapLinesPreference={defaultWrapLinesPreference}
          />
        }
        pagination={
          <Pagination
            {...paginationProps}
            ariaLabels={paginationLabels}
            onChange={(e) => handlePageChange(e)}
            openEnd={!allDataLoaded}
            disabled={paginationDisabled}
          />
        }
      />
    </>
  );
};

export default BrowseDatabasesTable;
